import React, { useState } from 'react';
import { Tabs, Typography, Card, Table, Button, Form, Input, Switch, Avatar, List, Tag, Modal, Select } from 'antd';
import {
  SafetyCertificateOutlined,
  UnlockOutlined,
  AppstoreOutlined,
  TeamOutlined,
  PlusOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

const ExternalDashboardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [activeTab, setActiveTab] = useState('1');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<string | undefined>(undefined);

  // Mock Data for Whitelisted Datasets
  const datasetColumns = [
    { title: 'Dataset Name', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (type: string) => <Tag color="blue">{type}</Tag> },
    { title: 'Access Level', dataIndex: 'access', key: 'access' },
    { title: 'Last Updated', dataIndex: 'lastUpdated', key: 'lastUpdated' },
    { title: 'Action', key: 'action', render: () => <a>Edit</a> },
  ];

  const datasetData = [
    { key: '1', name: 'Sales Data 2024', type: 'SQL', access: 'Read Only', lastUpdated: '2024-12-20' },
    { key: '2', name: 'Marketing Leads', type: 'CSV', access: 'Full Access', lastUpdated: '2024-12-22' },
  ];

  // Mock Data for Dashboards Library
  const dashboards = [
    { id: 1, title: 'Executive Overview', desc: 'High level metrics for executives', color: '#1890ff' },
    { id: 2, title: 'Sales Performance', desc: 'Detailed sales analysis', color: '#52c41a' },
    { id: 3, title: 'Customer Churn', desc: 'Analysis of customer retention', color: '#f5222d' },
    { id: 4, title: 'Inventory Levels', desc: 'Real-time inventory tracking', color: '#faad14' },
  ];

  // Mock Data for Users & Activity
  const activities = [
    { user: 'Alice', action: 'viewed', target: 'Executive Overview', time: '10 mins ago' },
    { user: 'Bob', action: 'edited', target: 'Sales Data 2024', time: '1 hour ago' },
    { user: 'Charlie', action: 'exported', target: 'Marketing Leads', time: '2 hours ago' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginTop: 0 }}>External Dashboards</Title>
        <Text type="secondary">Manage external access, whitelisted datasets, authentication, and usage activity.</Text>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab} 
        type="card"
        size="large"
        items={[
          {
            key: '1',
            label: (<span><SafetyCertificateOutlined /> Whitelisted Datasets</span>),
            children: (
              <Card title="Manage Whitelisted Datasets" extra={<Button type="primary" icon={<PlusOutlined />}>Add Dataset</Button>}>
                 <Table columns={datasetColumns} dataSource={datasetData} pagination={false} />
              </Card>
            )
          },
          {
            key: '2',
            label: (<span><UnlockOutlined /> Authentication Settings</span>),
            children: (
              <Card title="Authentication Configuration">
                <Form layout="vertical" style={{ maxWidth: 600 }}>
                  <Form.Item label="Auth Endpoint URL" name="authUrl" initialValue="https://auth.example.com/oauth/token">
                    <Input prefix={<UnlockOutlined />} />
                  </Form.Item>
                  <Form.Item label="Client ID" name="clientId" initialValue="client_12345abcdef">
                    <Input.Password />
                  </Form.Item>
                  <Form.Item label="Client Secret" name="clientSecret" initialValue="secret_98765zyxwv">
                    <Input.Password />
                  </Form.Item>
                  <Form.Item label="Enable SSO">
                    <Switch defaultChecked />
                  </Form.Item>
                   <Form.Item>
                    <Button type="primary">Save Changes</Button>
                  </Form.Item>
                </Form>
              </Card>
            )
          },
             {
            key: '3',
            label: (<span><AppstoreOutlined /> Default Library</span>),
            children: (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                 {dashboards.map(dash => (
                   <Card 
                    key={dash.id}
                    hoverable
                    actions={[<AppstoreOutlined key="open" />, <SafetyCertificateOutlined key="perm" />]}
                   >
                     <Card.Meta
                        avatar={<Avatar style={{ backgroundColor: dash.color }} icon={<AppstoreOutlined />} />}
                        title={dash.title}
                        description={dash.desc}
                     />
                   </Card>
                 ))}
                 <Button type="dashed" style={{ height: '100%', minHeight: '150px' }} onClick={() => setIsModalOpen(true)}>
                    <PlusOutlined /> Add Dashboard
                 </Button>
              </div>
            )
          },
          {
            key: '4',
            label: (<span><TeamOutlined /> Users & Activity</span>),
            children: (
               <Card title="Recent Activity">
                  <List
                    itemLayout="horizontal"
                    dataSource={activities}
                    renderItem={item => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={<a href="#">{item.user}</a>}
                          description={
                            <span>
                               {item.action} <strong>{item.target}</strong> - <Text type="secondary">{item.time}</Text>
                            </span>
                          }
                        />
                      </List.Item>
                    )}
                  />
               </Card>
            )
          }
        ]} 
      />
      <Modal
        title="Create New Dashboard Report"
        open={isModalOpen}
        onOk={() => {
            if (selectedDataset) {
                navigate(`/${workspaceId}/visualise/report/${selectedDataset}`);
            }
        }}
        onCancel={() => setIsModalOpen(false)}
        okText="Create"
        okButtonProps={{ disabled: !selectedDataset }}
      >
        <div style={{ padding: '24px 0' }}>
            <Text>Select a dataset to build your report from:</Text>
            <Select
                style={{ width: '100%', marginTop: '12px' }}
                placeholder="Choose a dataset"
                onChange={setSelectedDataset}
            >
                <Option value="1">Sales Orders</Option>
                <Option value="2">Customer Master</Option>
                <Option value="3">Inventory Logs</Option>
            </Select>
        </div>
      </Modal>
    </div>
  );
};

export default ExternalDashboardsPage;
